import { scroll } from "quasar";
import { Logger } from "@core/helpers/logger.helpers";

const { setVerticalScrollPosition } = scroll;

let _window;

// Protect against SSR environment
if (!process.env.SERVER) {
  _window = window;
}

let _scrollContainer = _window;
let _scrollOffset = 0;
let _lastScrollPosition = 0;

export class ScrollManager {
  /**
   * Allows you to set the scroll container for later use
   * @static
   * @param {HTMLElement} element
   */
  static configureScrollContainer(element = _window) {
    _scrollContainer = element;
  }

  static get scrollContainer() {
    return _scrollContainer;
  }

  /**
   * Allows you to set the scroll offset in case of different scroll container position
   * For instance mobile and desktop have different fixed headers
   * @static
   * @param {Number} offset
   */
  static configureScrollOffset(offset = 0) {
    _scrollOffset = offset;
  }

  static get scrollOffset() {
    return _scrollOffset;
  }

  /**
   * Remembers the current scroll position in a private property
   * The scroll position is related to the configured Scroll Container (default: window)
   */
  static rememberCurrentScrollPosition() {
    const lastScrollPosition = ScrollManager.getScrollPosition();
    Logger.log("ScrollManager.rememberCurrentScrollPosition", lastScrollPosition);
    _lastScrollPosition = lastScrollPosition;
  }

  /**
   * Forgets the last saved scroll position (set at 0)
   */
  static forgetLastScrollPosition() {
    Logger.log("ScrollManager.forgetLastScrollPosition");
    _lastScrollPosition = 0;
  }

  /**
   * Returns the last saved scroll position
   * @returns {Number} scroll position
   */
  static getLastScrollPosition() {
    Logger.log("ScrollManager.getLastScrollPosition", _lastScrollPosition);
    return _lastScrollPosition;
  }

  static goToLastScrollPosition({ withDelay = false } = {}) {
    const position = ScrollManager.getLastScrollPosition();
    ScrollManager.setVerticalScrollPosition({ position, withDelay });
  }

  static setVerticalScrollPosition({ position = 0, withDelay = false } = {}) {
    setVerticalScrollPosition(ScrollManager.scrollContainer, position, withDelay ? 250 : 0);
  }

  /**
   * Scroll container to top position
   * @static
   */
  static scrollToTop({ withDelay = false } = {}) {
    ScrollManager.setVerticalScrollPosition({ position: 0, withDelay });
  }

  /**
   * Scroll container to element
   * @param {HTMLElement} element
   * @static
   */
  static scrollToElement(element, withDelay = false, offset = 0) {
    if (element instanceof HTMLElement) {
      const elementTopPosition = element.getBoundingClientRect().top + ScrollManager.getScrollPosition() - ScrollManager.scrollOffset - offset;
      // we need to timeout in order to be sure everything is fine animation and size
      // no need to clearTimeout https://stackoverflow.com/questions/32580940/is-cleartimeout-necessary-after-settimeout-with-0ms/32581063
      setTimeout(() => {
        ScrollManager.setVerticalScrollPosition({ position: elementTopPosition, withDelay });
      }, 5);
    }
  }

  /**
   * Return the right scrollPosition depending on the scroll container
   * pageYOffset is for window, scrollTop is for any other element
   * @static
   * @returns {Number}
   */
  static getScrollPosition() {
    const scrollPosition = ScrollManager.scrollContainer.pageYOffset || ScrollManager.scrollContainer.scrollTop;
    // or 0 in order to ensure a number
    return scrollPosition || 0;
  }
}
